import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, MenuItem, TextField, Typography } from "@mui/material";
import Langs, { Localize } from "../../app/lang/langs";
import FormDialog, { FormButton, FormRow, formFieldSx } from "../../components/popups/FormDialog";
import { getCollabsNames } from "../../components/vcomp/vcompSlice";
import { setTeamManagers, ctAddTeamManager } from "../../components/teams/teamsSlice";
import { getManagersProfiles } from "../../components/permissions/permissionsSlice";
import { showErrorToast } from "../../app/utils/helpers";

// The team-managers list (Clients & Teams) carries the collab in `id_collab`; the Groupes
// managers list comes from getCollabs, where the row's `id` is the collab itself.
const managerCollabId = (manager) => manager.id_collab ?? manager.id;

/**
 * @param {boolean} props.visible
 * @param {Function} props.setVisible
 * @param {Array} props.prevManagers - the team's current managers, left out of the picker
 * @param {number} props.idTeam
 * @param {boolean} [props.managerProfilesOnly] - only offer profiles flagged `is_manager`
 */
export default function AddTeamManagersModal(props) {

    const dispatch = useDispatch();
    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const assignableProfiles = useSelector((state) => state.permissions.assignableProfiles);
    const [selectedManager, setSelectedManager] = useState(null);
    const [managerTypeId, setManagerTypeId] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        dispatch(getCollabsNames())
    }, [dispatch])

    useEffect(() => {
        if (props.visible && props.idTeam) {
            dispatch(getManagersProfiles({ idTeam: props.idTeam }))
        }
    }, [props.visible, props.idTeam])

    const filteredCollabs = useMemo(() => {
        const taken = new Set((props.prevManagers || []).map(managerCollabId));
        return (collabsNames || [])
            .filter(collab => !taken.has(collab.id))
            .map(({ id, nom, prenom, email }) => ({
                id,
                nom,
                prenom,
                email,
                fullName: `${prenom || ""} ${nom || ""}`.trim(),
            }));
    }, [collabsNames, props.prevManagers]);

    const managersProfiles = useMemo(() => (
        props.managerProfilesOnly
            ? (assignableProfiles || []).filter(profile => !!profile.is_manager)
            : (assignableProfiles || [])
    ), [assignableProfiles, props.managerProfilesOnly]);

    const managerType = managersProfiles.find(profile => profile.id === managerTypeId) || null;

    const handleClose = () => {
        if (saving) return;
        props.setVisible(false);
        setSelectedManager(null);
        setManagerTypeId("");
    };

    const handleAddManager = () => {
        if (selectedManager && props.idTeam && managerType) {
            const managerData = {
                id_team: props.idTeam,
                id_collab: selectedManager.id,
                id_manager_profile: managerType.id,
            };

            setSaving(true);
            dispatch(ctAddTeamManager(managerData))
                .then((actionResponse) => {
                    setSaving(false);
                    if (ctAddTeamManager.fulfilled.match(actionResponse)) {
                        const newManager = {
                            id: actionResponse.payload.id,
                            id_team: props.idTeam,
                            id_collab: selectedManager.id,
                            id_manager_profile: managerType.id,
                            type: managerType.name,
                            nom: selectedManager.nom,
                            prenom: selectedManager.prenom,
                            email: selectedManager.email,
                        };

                        dispatch(setTeamManagers([...props.prevManagers, newManager]));
                        props.setVisible(false);
                        setSelectedManager(null);
                        setManagerTypeId("");
                    }
                })
                .catch((error) => {
                    setSaving(false);
                    console.error('Erreur lors de l\'ajout du manager :', error);
                    showErrorToast('Erreur lors de l\'ajout du manager.');
                });
        }
    };

    return (
        <FormDialog
            open={props.visible}
            onClose={handleClose}
            title={<Langs str="ADD_MANAGER" />}
            actions={<>
                <FormButton
                    variant="primary"
                    onClick={handleAddManager}
                    loading={saving}
                    disabled={!selectedManager || !managerType}
                >
                    <Langs str="ADD" />
                </FormButton>
                <FormButton variant="secondary" onClick={handleClose} disabled={saving}>
                    <Langs str="CANCEL" />
                </FormButton>
            </>}
        >
            <FormRow label={<Langs str="SELECT_COLLAB" />} required>
                <Autocomplete
                    value={selectedManager}
                    onChange={(e, value) => setSelectedManager(value)}
                    options={filteredCollabs}
                    getOptionLabel={(option) => option.fullName}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    noOptionsText={Localize("No results")}
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} key={option.id}>
                            <div style={{ minWidth: 0 }}>
                                <div>{option.fullName}</div>
                                {option.email && (
                                    <Typography component="div" sx={{ fontSize: "0.75rem", color: "rgba(0, 40, 128, 0.5)" }}>
                                        {option.email}
                                    </Typography>
                                )}
                            </div>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={Localize("SELECT_COLLAB_PLACEHOLDER")}
                            sx={formFieldSx}
                        />
                    )}
                />
            </FormRow>
            <FormRow
                label={<Langs str="MANAGER_TYPE" />}
                required
                help={managersProfiles.length ? null : <Langs str="NO_MANAGER_PROFILE_AVAILABLE" />}
            >
                <TextField
                    select
                    fullWidth
                    size="small"
                    value={managerType ? managerTypeId : ""}
                    onChange={(e) => setManagerTypeId(e.target.value)}
                    disabled={!managersProfiles.length}
                    sx={formFieldSx}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (id) => {
                            const profile = managersProfiles.find(p => p.id === id);
                            return profile
                                ? profile.name
                                : <span style={{ color: "rgba(0, 40, 128, 0.4)" }}>{Localize("SELECT_PROFILE_PLACEHOLDER")}</span>;
                        },
                    }}
                >
                    {managersProfiles.map(profile => (
                        <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
                    ))}
                </TextField>
            </FormRow>
        </FormDialog>
    );
}
